import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query';

import type { CountryCode } from '../common/types';
import type { Client, CreditsafeRecord } from './types';

import { apiResourceSchema } from '../common/schemas';
import { fetchJson } from '../fetch';
import { QueryKeys } from '../keys';
import { creditsafeRecordSchema } from './schemas';

export type CreditsafeRecordSearchQuery = {
  companyName: string | null;
  countryCode: CountryCode;
  vatNumber: string | null;
};

const creditsafeRecordQueryOptions = (searchQuery: CreditsafeRecordSearchQuery | null) =>
  queryOptions({
    queryKey: QueryKeys.clients.creditsafe.search(searchQuery),
    queryFn: async () => {
      const { data: creditsafeRecord } = await fetchJson(`/clients/creditsafe-record`, {
        params: searchQuery!, // eslint-disable-line @typescript-eslint/no-non-null-assertion
        schema: apiResourceSchema(creditsafeRecordSchema),
      });

      return creditsafeRecord;
    },
    enabled: searchQuery !== null,
    staleTime: Infinity,
  });

export const useCreditsafeRecord = (searchQuery: CreditsafeRecordSearchQuery | null) => {
  return useQuery(creditsafeRecordQueryOptions(searchQuery));
};

/**
 * When a client is created, the creditsafe record is not yet available in the client resource.
 * Since we have recently fetched the record during the client creation, we try to retrieve it from the cache.
 */
export const useCachedCreditsafeRecord = (client: Client | null) => {
  const queryClient = useQueryClient();

  if (!client || client.clientType === 'private') return null;

  const searchQuery = {
    companyName: client.name,
    countryCode: client.addresses.find(({ type }) => type === 'headquarters')?.countryCode ?? client.addresses[0].countryCode,
    vatNumber: client.vatNumber,
  };

  // We can't use `getQueryData` because the client name includes the enterprise suffix (so there won't be an exact match).
  // Hence, we manually look for a matching query in the cache using a predicate function.
  const cachedCreditsafeQuery = queryClient.getQueriesData<CreditsafeRecord>({
    predicate: ({ queryKey }) => {
      if (queryKey[0] !== QueryKeys.clients.creditsafe.root[0] || queryKey[1] !== QueryKeys.clients.creditsafe.root[1]) {
        return false;
      }

      const originalSearchQuery = queryKey[2];

      if (
        typeof originalSearchQuery !== 'object' ||
        originalSearchQuery === null ||
        !('countryCode' in originalSearchQuery) ||
        !('vatNumber' in originalSearchQuery) ||
        !('companyName' in originalSearchQuery)
      ) {
        return false;
      }

      if (originalSearchQuery.countryCode !== searchQuery.countryCode) {
        return false;
      }

      if (originalSearchQuery.vatNumber !== null) {
        return originalSearchQuery.vatNumber === searchQuery.vatNumber;
      }

      if (typeof originalSearchQuery.companyName === 'string') {
        const cleanedCompanyName = searchQuery.companyName.replace(/[\W\D]/g, '').toLowerCase();
        const cleanedOriginalCompanyName = originalSearchQuery.companyName.replace(/[\W\D]/g, '').toLowerCase();

        return cleanedCompanyName.startsWith(cleanedOriginalCompanyName);
      }

      return false;
    },
  })[0];

  if (!cachedCreditsafeQuery) return null;

  return cachedCreditsafeQuery[1] ?? null;
};
